import { useId } from 'react';
import type { Connection, ConnectionCapability, ConnectionEndpoint } from '../../lib/connections-api';
import { Icon } from '../ui/Icon';
import styles from './Connections.module.css';

export type AbilitySelection = Pick<ConnectionCapability, 'id' | 'fingerprint' | 'selected' | 'policy'>;

export function abilitySelections(connection: Connection): AbilitySelection[] {
  return connection.capabilities.map(({ id, fingerprint, selected, policy }) =>
    ({ id, fingerprint, selected, policy }));
}

export function selectionFor(cap: ConnectionCapability, selections: AbilitySelection[]): AbilitySelection {
  const selection = selections.find((value) => value.id === cap.id && value.fingerprint === cap.fingerprint);
  return selection ?? { id: cap.id, fingerprint: cap.fingerprint, selected: false, policy: 'review' };
}

export function abilityName(title: string) {
  const words = title.replaceAll('_', ' ').trim();
  return words ? words[0]!.toUpperCase() + words.slice(1) : 'Untitled ability';
}

export function endpointName(endpoint: ConnectionEndpoint) {
  const identity = endpoint.metadata.identity;
  if (typeof identity === 'string' && identity.trim()) return identity;
  if (identity && typeof identity === 'object' && 'name' in identity && typeof identity.name === 'string')
    return identity.name;
  return endpoint.protocol === 'mcp' ? 'MCP server' : 'A2A agent';
}

export function endpointReady(endpoint: ConnectionEndpoint) {
  return endpoint.status === 'ready' && !!endpoint.valid_until && Date.parse(endpoint.valid_until) > Date.now();
}

export function selectedAbilities(connection: Connection, selections: AbilitySelection[]) {
  return connection.capabilities.filter((cap) => cap.available && selectionFor(cap, selections).selected);
}

export function selectionPayload(connection: Connection, selections: AbilitySelection[]) {
  return connection.capabilities.map((cap) => {
    const selection = selectionFor(cap, selections);
    return { id: cap.id, selected: selection.selected && cap.available, policy: selection.policy };
  });
}

export function ConnectionAbilities({ connection, selections, onChange, disabled = false, selectionRequired = false }: {
  connection: Connection;
  selections: AbilitySelection[];
  onChange: (selections: AbilitySelection[]) => void;
  disabled?: boolean;
  selectionRequired?: boolean;
}) {
  const selectionHint = useId();
  const available = connection.capabilities.filter((cap) => cap.available);
  const selected = selectedAbilities(connection, selections).length;
  const needsSelection = selectionRequired && selected === 0;
  const update = (value: AbilitySelection) => onChange([
    ...selections.filter((selection) => selection.id !== value.id), value,
  ]);
  return <div className={styles.abilities} role="group" aria-label="Connection abilities"
    aria-describedby={needsSelection ? selectionHint : undefined}>
    <div className={styles.abilityToolbar}>
      <span className={needsSelection ? styles.selectionRequiredCount : undefined}>{selected} of {available.length} selected</span>
      <button type="button" disabled={disabled || !available.length} onClick={() => onChange(
        connection.capabilities.map((cap) => ({
          ...selectionFor(cap, selections), selected: cap.available && selected !== available.length,
        })),
      )}>{selected === available.length && available.length ? 'Clear selection' : 'Select all'}</button>
    </div>
    {needsSelection && <p id={selectionHint} role="alert" className={styles.selectionRequiredMessage}>
      <Icon name="alert-circle" size={18} />
      <span>{available.length
        ? 'Select at least one ability below to turn on this connection.'
        : 'No abilities are available to select. Check the connection or reconnect an endpoint.'}</span>
    </p>}
    {!connection.capabilities.length && <p className={styles.muted}>No abilities were discovered. Check the endpoint in connection settings.</p>}
    {connection.capabilities.map((cap) => {
      const selection = selectionFor(cap, selections);
      const requiresReview = cap.kind === 'agent_skill' || connection.endpoints
        .find((endpoint) => endpoint.id === cap.endpoint_id)?.metadata.reviewRequiredTools?.includes(cap.remote_id);
      return <div key={cap.id} className={styles.abilityRow} data-selected={selection.selected && cap.available}>
        <label className={styles.abilityChoice}>
          <input type="checkbox" checked={selection.selected && cap.available}
            disabled={disabled || !cap.available}
            onChange={(event) => update({ ...selection, selected: event.target.checked })} />
          <span><strong>{abilityName(cap.title)}</strong>
            <span>{!cap.available ? 'Currently unavailable' : cap.kind === 'agent_skill' ? 'Agent task · Review required' : requiresReview ? 'Review required' : 'Tool'}</span>
            {cap.description && <span className={styles.abilityDescription}>{cap.description}</span>}
          </span>
        </label>
        <div className={styles.abilityPermission}>
          {requiresReview ? <span>Review each request</span> : <select
            aria-label={`Permission for ${abilityName(cap.title)}`}
            value={selection.policy} disabled={disabled || !cap.available || !selection.selected}
            onChange={(event) => update({ ...selection, policy: event.target.value as 'read' | 'review' })}>
            <option value="review">Review each request</option>
            <option value="read">Allow trusted reads</option>
          </select>}
        </div>
        <details className={styles.abilityDetails}>
          <summary>About this ability</summary>
          <p>{cap.description || 'No description supplied by the service.'}</p>
          {selection.policy === 'read' && <p>Trusted reads can run without asking. Choose this only for a read ability you trust; the service controls what its tools do.</p>}
          <dl className={styles.fields}><dt>Tool ID</dt><dd><code>{cap.remote_id}</code></dd></dl>
          <details><summary>Input schema</summary><pre className={styles.result}>{JSON.stringify(cap.input_schema, null, 2)}</pre></details>
        </details>
      </div>;
    })}
  </div>;
}
